// Client-side, per-browser-session tour state. Not tied to the logged-in account.
//
// Behavior: same browser session -> tour won't repeat; browser closed/reopened or a
// different browser -> tour shows again on next login.
//
// To make this "once per browser, forever" instead, swap the line below to `localStorage`.
export const onboardingStorage = sessionStorage;

export const ONBOARDING_STATUS = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
};

// Keys are namespaced per portal so Client / Graphe tours (present or future) never collide.
export const getOnboardingKeys = (namespace) => ({
  trigger: `bb_onboarding_trigger_${namespace}`,
  status: `bb_onboarding_status_${namespace}`,
  step: `bb_onboarding_step_${namespace}`,
});

/**
 * Call this right after a successful login() call (before navigating away from the
 * login page) to arm the tour for the very next dashboard mount this session.
 */
export const armOnboardingTrigger = (namespace) => {
  const { trigger } = getOnboardingKeys(namespace);
  onboardingStorage.setItem(trigger, '1');
};

// Login-only chained tour: dashboard → profile → Wedding form → home.
// The "?" replay button never touches this key, so finishing a per-page replay
// cannot navigate into the next page's tour.
export const ONBOARDING_CHAIN_PHASE = {
  DASHBOARD: 'dashboard',
  PROFILE: 'profile',
  FORM: 'form',
};

const CLIENT_CHAIN_KEY = 'bb_onboarding_chain_client';

export const getOnboardingChainPhase = () => onboardingStorage.getItem(CLIENT_CHAIN_KEY);

const setOnboardingChainPhase = (phase) => {
  onboardingStorage.setItem(CLIENT_CHAIN_KEY, phase);
};

export const clearOnboardingChain = () => {
  onboardingStorage.removeItem(CLIENT_CHAIN_KEY);
};

/**
 * Arm the login-only chain alongside `armOnboardingTrigger('client')`.
 * No-ops if a chain is already in progress (resume after refresh/logout) or if
 * the dashboard tour was already completed/skipped this session — same gate the
 * auto-start trigger uses, so a second login in the same tab does not re-run it.
 */
export const startOnboardingChain = () => {
  if (getOnboardingChainPhase()) return;
  const { status } = getOnboardingKeys('client');
  const current = onboardingStorage.getItem(status);
  if (current === ONBOARDING_STATUS.COMPLETED || current === ONBOARDING_STATUS.SKIPPED) return;
  setOnboardingChainPhase(ONBOARDING_CHAIN_PHASE.DASHBOARD);
};

/**
 * Called from a tour's `onFinish`. Returns a path to navigate to, or null.
 * Only advances when this finish belongs to the login chain (phase matches);
 * Skip abandons the rest of the chain; "?" replay never sets the phase so it
 * stays on the current page.
 */
export const handleOnboardingChainFinish = (expectedPhase, status) => {
  if (getOnboardingChainPhase() !== expectedPhase) return null;

  if (status !== ONBOARDING_STATUS.COMPLETED) {
    clearOnboardingChain();
    return null;
  }

  if (expectedPhase === ONBOARDING_CHAIN_PHASE.DASHBOARD) {
    setOnboardingChainPhase(ONBOARDING_CHAIN_PHASE.PROFILE);
    armOnboardingTrigger('client-profile');
    return '/Client/dashboard/Client_profile';
  }
  if (expectedPhase === ONBOARDING_CHAIN_PHASE.PROFILE) {
    setOnboardingChainPhase(ONBOARDING_CHAIN_PHASE.FORM);
    armOnboardingTrigger('client-onboarding-form');
    return '/Client/dashboard/Onboarding_Form/Wedding';
  }
  if (expectedPhase === ONBOARDING_CHAIN_PHASE.FORM) {
    clearOnboardingChain();
    return '/Client/dashboard/Client_home';
  }
  return null;
};

/** If the user refreshed/navigated away mid-chain, send them back to that page. */
export const getOnboardingChainResumePath = (currentPathname = '') => {
  const phase = getOnboardingChainPhase();
  if (phase === ONBOARDING_CHAIN_PHASE.PROFILE && !currentPathname.includes('Client_profile')) {
    return '/Client/dashboard/Client_profile';
  }
  if (phase === ONBOARDING_CHAIN_PHASE.FORM && !currentPathname.includes('Onboarding_Form')) {
    return '/Client/dashboard/Onboarding_Form/Wedding';
  }
  return null;
};

// Broadcast used to manually replay an already-mounted tour (e.g. from a "help" button),
// without touching the one-shot `trigger` key that gates the automatic first-login run.
export const ONBOARDING_REPLAY_EVENT = 'bb-onboarding-replay';

/**
 * Manually re-run a namespace's tour from the beginning, from anywhere in the app.
 * Any `OnboardingTour` instance mounted with a matching `namespace` will restart itself.
 * This intentionally does NOT touch the stored completed/skipped status until the replay
 * itself finishes, so it can't interfere with the automatic first-login trigger logic.
 */
export const replayOnboardingTour = (namespace) => {
  window.dispatchEvent(new CustomEvent(ONBOARDING_REPLAY_EVENT, { detail: { namespace } }));
};
